import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { approvalsApi } from '@/api/approvals.api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { ButtonLink } from '@/components/ui/ButtonLink';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { Select } from '@/components/ui/Select';
import { Modal } from '@/components/ui/Modal';
import { TooltipText } from '@/components/ui/Tooltip';
import { useAccessibleApprovalModules } from '@/hooks/useAccessibleApprovalModules';
import { dataTablePageProps, paginateClientList, usePagination } from '@/hooks/usePagination';
import { resolveApprovalModuleFilter } from '@/modules/approvals/approvalModuleOptions';
import type { ApprovalInstance, ApprovalStatus } from '@/types/approvals';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';
import { ApprovalTimeline } from './components/ApprovalTimeline';
import { ApprovalStatusBadge } from './components/ApprovalStatusBadge';
import { ApprovalActionPanel } from './components/ApprovalActionPanel';

const STATUS_OPTIONS: { value: ApprovalStatus; label: string }[] = [
  { value: 'PENDING', label: 'Pending' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'REJECTED', label: 'Rejected' },
  { value: 'RETURNED', label: 'Returned' },
  { value: 'ESCALATED', label: 'Escalated' },
  { value: 'ALL', label: 'All' },
];

export function ApprovalsInboxPage() {
  const [searchParams] = useSearchParams();
  const { selectOptions, modules } = useAccessibleApprovalModules();
  const pagination = usePagination();
  const [data, setData] = useState<ApprovalInstance[]>([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<ApprovalStatus>('PENDING');
  const [moduleFilter, setModuleFilter] = useState(
    () => searchParams.get('ownerModule') ?? '',
  );
  const [onlyMine, setOnlyMine] = useState(
    searchParams.get('mineOnly') === 'true' || searchParams.get('mine') === '1',
  );
  const [commentsById, setCommentsById] = useState<Record<number, string>>({});
  const [actingId, setActingId] = useState<number | null>(null);
  const [selected, setSelected] = useState<ApprovalInstance | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const status = onlyMine ? 'PENDING' : statusFilter === 'ALL' ? undefined : statusFilter;
      const rows = await approvalsApi.inbox(status, moduleFilter || undefined, onlyMine);
      setData(rows);
      pagination.applyClientCount(rows.length);
    } catch (err) {
      showApiError(err, 'Failed to load approvals');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, moduleFilter, onlyMine, pagination]);

  useEffect(() => {
    setModuleFilter((current) => resolveApprovalModuleFilter(current, modules));
  }, [modules]);

  useEffect(() => {
    pagination.resetPage();
  }, [statusFilter, moduleFilter, onlyMine, pagination]);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (id: number, action: string) => {
    setActingId(id);
    try {
      if (action === 'RESUBMIT') {
        const result = await approvalsApi.resubmit(id);
        notify.success(`Resubmitted · ${result.status.toLowerCase()}`);
      } else {
        const result = await approvalsApi.act(id, action, commentsById[id] || undefined);
        notify.success(`Request ${result.status.toLowerCase()}`);
      }
      setSelected(null);
      load();
    } catch (err) {
      showApiError(err, 'Approval action failed');
    } finally {
      setActingId(null);
    }
  };

  const columns: Column<ApprovalInstance>[] = [
    { key: 'id', header: '#', className: 'w-12' },
    { key: 'ownerModule', header: 'Module' },
    { key: 'entityType', header: 'Action' },
    {
      key: 'title',
      header: 'Title',
      render: (row) => (
        <TooltipText
          text={row.title ?? row.entityRef ?? `#${row.entityId}`}
          className="block max-w-[200px]"
        />
      ),
    },
    {
      key: 'currentStepName',
      header: 'Step',
      render: (row) => row.currentStepName ?? `Step ${row.currentStepOrder}`,
    },
    {
      key: 'approvalStatus',
      header: 'Status',
      render: (row) => <ApprovalStatusBadge status={row.approvalStatus} />,
    },
    {
      key: 'slaDeadline',
      header: 'SLA',
      render: (row) =>
        row.slaDeadline ? new Date(row.slaDeadline).toLocaleString() : '—',
    },
    {
      key: 'id',
      header: 'Actions',
      render: (row) => (
        <div className="flex min-w-[200px] flex-col gap-2">
          <Button size="sm" variant="ghost" className="self-start" onClick={() => setSelected(row)}>
            Timeline
          </Button>
          <ApprovalActionPanel
            row={row}
            comments={commentsById[row.id] ?? ''}
            acting={actingId === row.id}
            onCommentsChange={(v) => setCommentsById((prev) => ({ ...prev, [row.id]: v }))}
            onAct={(action) => act(row.id, action)}
          />
        </div>
      ),
    },
  ];

  return (
    <div className="page-content">
      <PageHeader
        title="My Approvals"
        description="Global approval inbox across all Fattah modules"
        breadcrumbs={[
          { label: 'Work', to: '/work' },
          { label: 'Approvals' },
        ]}
        actions={
          <div className="flex flex-wrap gap-2">
            <ButtonLink to="/approvals/builder" variant="secondary">
              Workflow Builder
            </ButtonLink>
            <ButtonLink to="/approvals/matrix" variant="secondary">
              Approval Matrix
            </ButtonLink>
          </div>
        }
      />

      <div className="card mb-4 grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-4">
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-slate-600 dark:text-slate-400">View</span>
          <Select
            value={onlyMine ? 'mine' : 'all'}
            onChange={(v) => setOnlyMine(v === 'mine')}
            options={[
              { value: 'mine', label: 'My queue only' },
              { value: 'all', label: 'All requests' },
            ]}
          />
        </label>
        {!onlyMine ? (
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-slate-600 dark:text-slate-400">Status</span>
            <Select
              value={statusFilter}
              onChange={(v) => setStatusFilter(v as ApprovalStatus)}
              options={STATUS_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
            />
          </label>
        ) : null}
        {selectOptions.length > 0 ? (
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-slate-600 dark:text-slate-400">Module</span>
            <Select
              value={moduleFilter}
              onChange={setModuleFilter}
              options={selectOptions}
            />
          </label>
        ) : null}
        <div className="flex items-end">
          <Button variant="secondary" onClick={load} disabled={loading} className="w-full sm:w-auto">
            Refresh
          </Button>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={paginateClientList(data, pagination.page, pagination.size)}
        keyField="id"
        loading={loading}
        emptyMessage="No approval requests in this view"
        {...dataTablePageProps(pagination)}
      />

      <Modal
        open={!!selected}
        onClose={() => setSelected(null)}
        title={selected?.title ?? 'Approval timeline'}
      >
        {selected ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
              <div>
                <span className="text-slate-500">Module</span>
                <p className="font-medium">{selected.ownerModule}</p>
              </div>
              <div>
                <span className="text-slate-500">Status</span>
                <p><ApprovalStatusBadge status={selected.approvalStatus} /></p>
              </div>
              <div>
                <span className="text-slate-500">Workflow</span>
                <p>{selected.workflowName ?? '—'}</p>
              </div>
              <div>
                <span className="text-slate-500">Current step</span>
                <p>{selected.currentStepName ?? selected.currentStepOrder}</p>
              </div>
            </div>
            <ApprovalActionPanel
              row={selected}
              comments={commentsById[selected.id] ?? ''}
              acting={actingId === selected.id}
              onCommentsChange={(v) => setCommentsById((prev) => ({ ...prev, [selected.id]: v }))}
              onAct={(action) => act(selected.id, action)}
            />
            <ApprovalTimeline actions={selected.timeline ?? []} />
          </div>
        ) : null}
      </Modal>
    </div>
  );
}
